import { z } from 'zod';
import { isNativePlatform } from '../utils/platform';

/**
 * D-10/D-14 capture hook for store screenshots.
 *
 * This module is inert unless a native shim (simulator-only on iOS,
 * debuggable-only on Android — see Phase 81 Plan 05) injects a validated
 * payload into `window.__REFEREE_SCREENSHOT__`. On the web, and for any
 * payload that fails validation, app behaviour is byte-for-byte unchanged.
 * It is never user-reachable.
 */

declare global {
  interface Window {
    __REFEREE_SCREENSHOT__?: string;
  }
}

const BASE64_PATTERN = /^[A-Za-z0-9+/=]{1,4096}$/;
const QUESTION_ID_PATTERN = /^[A-Za-z0-9_.-]{1,40}$/;
const ANSWER_PATTERN = /^[A-Za-z0-9_-]{1,10}$/;

const scenarioSchema = z
  .object({
    lang: z.enum(['en', 'de', 'es', 'pt']),
    catalog: z.enum(['ihf_07_2025', 'ihf_08_2019', 'ihf_05_2024']),
    questionId: z.string().regex(QUESTION_ID_PATTERN),
    state: z.enum(['picker', 'question', 'feedback']),
    answers: z.array(z.string().regex(ANSWER_PATTERN)).max(6),
  })
  .strict();

export type ScreenshotScenario = z.infer<typeof scenarioSchema>;

export function parseScreenshotScenario(raw: unknown): ScreenshotScenario | null {
  if (typeof raw !== 'string' || !BASE64_PATTERN.test(raw)) {
    return null;
  }

  let parsedJson: unknown;
  try {
    const binary = atob(raw);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const text = new TextDecoder().decode(bytes);
    parsedJson = JSON.parse(text);
  } catch {
    return null;
  }

  const result = scenarioSchema.safeParse(parsedJson);
  return result.success ? result.data : null;
}

let memoizedScenario: ScreenshotScenario | null | undefined;

export function getScreenshotScenario(): ScreenshotScenario | null {
  if (memoizedScenario !== undefined) {
    return memoizedScenario;
  }

  if (!isNativePlatform()) {
    memoizedScenario = null;
    return memoizedScenario;
  }

  memoizedScenario = parseScreenshotScenario(
    (window as { __REFEREE_SCREENSHOT__?: unknown }).__REFEREE_SCREENSHOT__,
  );
  return memoizedScenario;
}
